import { computed, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useRoute,
  useRouter,
  type LocationQuery,
  type RouteLocationNormalized,
  type RouteLocationRaw,
} from 'vue-router';

// The router imports this file, so it stays free of report data code.

/** The route names of the reports area. Each report has its own page. */
export const REPORT_ROUTES = {
  home: 'reports',
  doctors: 'reportsDoctors',
  doctor: 'reportsDoctor',
  premiums: 'reportsPremiums',
  scheduleGroups: 'reportsScheduleGroups',
  scheduleGroup: 'reportsScheduleGroup',
  scheduleLine: 'reportsScheduleLine',
  phoneNumbers: 'reportsPhoneNumbers',
} as const;

/** A report that has a card on the reports home page. */
export type ReportKey = 'doctors' | 'premiums' | 'scheduleGroups' | 'scheduleLine' | 'phoneNumbers';

/** One card on the reports home page. Text values are i18n keys. */
export interface ReportCard {
  key: ReportKey;
  routeName: string;
  /** A PrimeIcons class. */
  icon: string;
  title: string;
  description: string;
}

/** A group of report cards on the home page. Text values are i18n keys. */
export interface ReportSection {
  /** Also the section's anchor on the home page (/reports#ivr). */
  key: string;
  title: string;
  description: string;
  reports: ReportCard[];
}

/** The reports home page, in display order. Add a section here to show it. */
export const REPORT_SECTIONS: ReportSection[] = [
  {
    key: 'ivr',
    title: 'reports.home.ivr.title',
    description: 'reports.home.ivr.description',
    reports: [
      {
        key: 'doctors',
        routeName: REPORT_ROUTES.doctors,
        icon: 'pi pi-users',
        title: 'reports.home.ivr.doctors.title',
        description: 'reports.home.ivr.doctors.description',
      },
      {
        key: 'premiums',
        routeName: REPORT_ROUTES.premiums,
        icon: 'pi pi-dollar',
        title: 'reports.home.ivr.premiums.title',
        description: 'reports.home.ivr.premiums.description',
      },
      {
        key: 'scheduleGroups',
        routeName: REPORT_ROUTES.scheduleGroups,
        icon: 'pi pi-sitemap',
        title: 'reports.home.ivr.scheduleGroups.title',
        description: 'reports.home.ivr.scheduleGroups.description',
      },
      {
        key: 'scheduleLine',
        routeName: REPORT_ROUTES.scheduleLine,
        icon: 'pi pi-list',
        title: 'reports.home.ivr.scheduleLine.title',
        description: 'reports.home.ivr.scheduleLine.description',
      },
      {
        key: 'phoneNumbers',
        routeName: REPORT_ROUTES.phoneNumbers,
        icon: 'pi pi-phone',
        title: 'reports.home.ivr.phoneNumbers.title',
        description: 'reports.home.ivr.phoneNumbers.description',
      },
    ],
  },
];

/** The home page's sections: only those with at least one report. */
export function homeSections(sections: ReportSection[] = REPORT_SECTIONS): ReportSection[] {
  return sections.filter((section) => section.reports.length > 0);
}

/**
 * The query keys every reports page shares: the period (from, to), the
 * timezone (tz) and the picked inbound number (number). Links between report
 * pages carry them, so moving around keeps what is picked.
 */
export const REPORT_SHARED_KEYS = ['from', 'to', 'tz', 'number'] as const;

/** Only the shared keys of a query (see REPORT_SHARED_KEYS). */
export function sharedReportQuery(query: LocationQuery): LocationQuery {
  const shared: LocationQuery = {};
  for (const key of REPORT_SHARED_KEYS) {
    const value = query[key];
    if (value !== undefined) shared[key] = value;
  }
  return shared;
}

/** A string query value, or null when it is missing, empty or repeated. */
function queryString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** A doctor ID as text, or null for anything that is not one. */
function parseDoctorId(value: unknown): number | null {
  return typeof value === 'string' && /^[1-9]\d{0,14}$/.test(value) ? Number(value) : null;
}

/**
 * Where an old tabbed link (/reports?tab=<tab>&...) now lives, keeping its
 * other query values. Undefined when the link has no ?tab= and needs no
 * redirect. An unknown tab goes to the home page.
 */
export function legacyTabRedirect(to: RouteLocationNormalized): RouteLocationRaw | undefined {
  if (to.query.tab === undefined) return undefined;
  const query: LocationQuery = { ...to.query };
  const tab = query.tab;
  delete query.tab;

  switch (tab) {
    case 'premiums':
      return { name: REPORT_ROUTES.premiums, query };
    case 'phoneNumber':
      return { name: REPORT_ROUTES.phoneNumbers, query };
    case 'scheduleLine':
      return { name: REPORT_ROUTES.scheduleLine, query };
    case 'doctors': {
      const doctorId = parseDoctorId(query.doctor);
      delete query.doctor;
      return doctorId === null
        ? { name: REPORT_ROUTES.doctors, query }
        : { name: REPORT_ROUTES.doctor, params: { doctorId: String(doctorId) }, query };
    }
    case 'scheduleGroups': {
      const group = queryString(query.group);
      delete query.group;
      // ReportsView lists every group when the key is not a valid groupKey.
      return group
        ? { name: REPORT_ROUTES.scheduleGroup, params: { groupKey: group }, query }
        : { name: REPORT_ROUTES.scheduleGroups, query };
    }
    default:
      return { name: REPORT_ROUTES.home, query };
  }
}

/** The report a reports route belongs to, or null for the home page. */
export function reportOfRoute(name: unknown): ReportKey | null {
  switch (name) {
    case REPORT_ROUTES.doctors:
    case REPORT_ROUTES.doctor:
      return 'doctors';
    case REPORT_ROUTES.premiums:
      return 'premiums';
    case REPORT_ROUTES.scheduleGroups:
    case REPORT_ROUTES.scheduleGroup:
      return 'scheduleGroups';
    case REPORT_ROUTES.scheduleLine:
      return 'scheduleLine';
    case REPORT_ROUTES.phoneNumbers:
      return 'phoneNumbers';
    default:
      return null;
  }
}

/** True for any reports route, the home page included. */
export function isReportsRoute(name: unknown): boolean {
  return name === REPORT_ROUTES.home || reportOfRoute(name) !== null;
}

/** One breadcrumb. A crumb with no route is the page you are on. */
export interface ReportCrumb {
  label: string;
  route?: RouteLocationRaw;
}

/**
 * The breadcrumb for a report page: Reports > IVR operations > the report.
 * On a drill-down, current names the open doctor or group, and the report
 * crumb links back to its list (the doctors list keeps its group filter).
 * Every link keeps the shared period, timezone and number.
 */
export function useReportBreadcrumb(report: Ref<ReportKey>, current: Ref<string | null>) {
  const { t } = useI18n();
  const route = useRoute();

  const items = computed<ReportCrumb[]>(() => {
    const section = REPORT_SECTIONS.find((s) => s.reports.some((r) => r.key === report.value));
    const card = section?.reports.find((r) => r.key === report.value);
    const shared = sharedReportQuery(route.query);
    const crumbs: ReportCrumb[] = [
      { label: t('reports.title'), route: { name: REPORT_ROUTES.home, query: shared } },
    ];
    if (section) {
      crumbs.push({
        label: t(section.title),
        route: { name: REPORT_ROUTES.home, query: shared, hash: `#${section.key}` },
      });
    }
    if (!card) return crumbs;
    if (current.value === null) {
      crumbs.push({ label: t(card.title) });
      return crumbs;
    }
    const group = queryString(route.query.group);
    const query = { ...shared, ...(report.value === 'doctors' && group ? { group } : {}) };
    crumbs.push({ label: t(card.title), route: { name: card.routeName, query } });
    crumbs.push({ label: current.value });
    return crumbs;
  });

  return { items };
}

/**
 * Drill-down links between the ops screens. Each screen has its own route,
 * and its filters are in the URL so they survive a reload:
 * - /reports/ivr/schedule-groups/<source>-<id> opens a schedule group (see
 *   groupKey).
 * - /reports/ivr/doctors/<id> opens a doctor.
 * - ?group=<source>-<id> is the group filter on the doctors, doctor and
 *   schedule line screens.
 * - ?line=<source>-<id> picks the line on the schedule line screen, and is
 *   the line filter on the doctor screen.
 * Opening a group or doctor adds a history entry; changing a filter does not.
 * Every link keeps the shared period, timezone and number.
 */
export function useReportDrilldown() {
  const route = useRoute();
  const router = useRouter();

  /** The ?group= value, or null when none is set. */
  const groupParam = computed(() => queryString(route.query.group));

  /** The ?line= value, or null when none is set. */
  const lineParam = computed(() => queryString(route.query.line));

  /** The open doctor's ID from the path, or null when none is open. */
  const doctorParam = computed(() => parseDoctorId(route.params.doctorId));

  /** The open schedule group's groupKey from the path, or null. */
  const openGroupParam = computed(() => queryString(route.params.groupKey));

  /** The shared keys, then only the filters that are set. */
  function filters(group: string | null, line: string | null): LocationQuery {
    return {
      ...sharedReportQuery(route.query),
      ...(group ? { group } : {}),
      ...(line ? { line } : {}),
    };
  }

  /** Opens a schedule group's screen. */
  function openGroup(key: string): void {
    void router.push({
      name: REPORT_ROUTES.scheduleGroup,
      params: { groupKey: key },
      query: filters(null, null),
    });
  }

  /** Swaps the open schedule group for another. */
  function selectGroup(key: string): void {
    void router.replace({
      name: REPORT_ROUTES.scheduleGroup,
      params: { groupKey: key },
      query: filters(null, null),
    });
  }

  /** Sets the schedule line screen's filters. Null leaves a filter out. */
  function setLineFilters(group: string | null, line: string | null): void {
    void router.replace({ name: REPORT_ROUTES.scheduleLine, query: filters(group, line) });
  }

  /** Opens a doctor's screen, with the group and line filters when given. */
  function openDoctor(doctorId: number, group: string | null, line: string | null = null): void {
    void router.push({
      name: REPORT_ROUTES.doctor,
      params: { doctorId: String(doctorId) },
      query: filters(group, line),
    });
  }

  /**
   * Sets the doctors screens' filters. A null doctor lists every doctor; a
   * null group or line leaves that filter out.
   */
  function setDoctorFilters(doctorId: number | null, group: string | null, line: string | null) {
    void router.replace(
      doctorId === null
        ? { name: REPORT_ROUTES.doctors, query: filters(group, null) }
        : {
            name: REPORT_ROUTES.doctor,
            params: { doctorId: String(doctorId) },
            query: filters(group, line),
          },
    );
  }

  return {
    groupParam,
    lineParam,
    doctorParam,
    openGroupParam,
    openGroup,
    selectGroup,
    setLineFilters,
    openDoctor,
    setDoctorFilters,
  };
}
